import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { IAudienceUserQuery } from 'app/library/audience-users/interfaces/audience-user.query.interface';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-users/interfaces/audience-users-action.action-payload.interface';
import { EngageMoveExperienceMembersDialogComponent } from 'app/library/grow/dialogs/engage-move-experience-members-dialog/engage-move-experience-members-dialog.component';
import { AddUsersToExperiencesSuccess, DeleteExperienceUsersSuccess, MoveExperienceUsersSuccess } from 'app/library/experience-user/actions';
import { ExperienceUserExtended } from 'app/library/experience-user/models/experience-user.extended.model';
import { ExperienceUser } from 'app/library/experience-user/models/experience-user.model';
import { SortExperienceUsers } from 'app/library/experience-users/actions';
import { ExperienceMemberDataSource } from 'app/library/experience-users/components/list/experience-datasource';
import { ExperienceUsersService } from 'app/library/experience-users/services/experience-users.service';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FuseConfigService } from 'app/projects/fuse/src/lib/services/config.service';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { UserStatuses } from 'app/projects/user/src/lib/models/user-statuses';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-experience-users-list',
    templateUrl: './experience-users-list.component.html',
    styleUrls: ['./experience-users-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ExperienceUsersListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    sortBy: string;

    @Input()
    sortOrder: SortDirection;

    @Input()
    user$: Observable<IAudienceUserQuery[]>;

    @Input()
    loadUser$: Observable<boolean>;

    @Input()
    filteredUsers$: Observable<ExperienceUserExtended[]>;

    @Input()
    selectedUserId$: Observable<string[]>;

    @Input()
    isFetchingExperienceUsers$: Observable<boolean>;

    @Input()
    searchTerm$: Observable<string>;
    searchTerm: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    experienceId: string = '';

    @Input()
    experienceName: string = '';

    @Output()
    edit = new EventEmitter<ExperienceUser>();

    @Output()
    select = new EventEmitter<ExperienceUser>();

    @Output()
    toggle = new EventEmitter();

    profileId = this._store.selectSnapshot(UserState).me;

    fuseConfig: any;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    dataSource: ExperienceMemberDataSource;
    displayedColumns = ['checkbox', 'avatar', 'firstName', 'lastName', 'email', 'buttons'];
    selectedUserIds: string[] = [];
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    engageMoveUsersDialogRef: MatDialogRef<EngageMoveExperienceMembersDialogComponent>;
    userStatuses = UserStatuses;

    /**
     * Constructor
     */
    constructor(
        private _experienceService: ExperienceService,
        private _experienceUsersService: ExperienceUsersService,
        private _translateService: TranslateService,
        private _store: Store,
        private _action$: Actions,
        public _matDialog: MatDialog,
        private _fuseConfigService: FuseConfigService
    ) {
        super();

        // Subscribe to config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.fuseConfig = config;
        });
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this.loadUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadUsersPage(resetPageIndex);
            });

        this.user$
            .pipe(
                filter((users) => !!users),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((experienceUsers) => {
                this.checkboxes = {};

                experienceUsers.map((audienceUser) => {
                    this.checkboxes[audienceUser.id] = this.selectedUserIds.includes(audienceUser.id);
                });
            });

        this.selectedUserId$
            .pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                for (const id in this.checkboxes) {
                    if (!this.checkboxes.hasOwnProperty(id)) {
                        continue;
                    }

                    this.checkboxes[id] = selectedUserIds.includes(id);
                }

                this.selectedUserIds = selectedUserIds;
            });

        this.dataSource = new ExperienceMemberDataSource(this.filteredUsers$, this._store, this._action$);

        this._action$
            .pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(AddUsersToExperiencesSuccess, DeleteExperienceUsersSuccess, MoveExperienceUsersSuccess))
            .subscribe(() => {
                this.loadUsersPage(true);
            });

        setTimeout(() => {
            this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                const itemsleft = length - pageIndex * pageSize;
                const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                const contentElementHeight = document.getElementById('container-2').offsetHeight;
                const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                setTimeout(() => {
                    document.getElementById('container-3').scrollTop = scrollTop;
                });
            });

            this.loadUsersPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortExperienceUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });
        });
    }

    ngAfterViewInit(): void {
        this.searchTerm$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((searchTerm, index) => {
                    this.searchTerm = searchTerm || '';
                    return index > 0;
                })
            )
            .subscribe((searchTerm) => {
                this.loadUsersPage(true);
            });
    }

    deleteExperienceUser(experienceUserId: string): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'ENGAGE.EXPERIENCE.DIALOGS.REMOVE_USER.TITLE.SINGULAR';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('ENGAGE.EXPERIENCE.DIALOGS.REMOVE_USER.MESSAGE.SINGULAR', {
            experienceName: this.experienceName,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const experienceUserIdAsArray = [{ id: experienceUserId }] as [{ id: string }];
                await this._experienceUsersService.deleteExperienceUsers({ experienceUsers: experienceUserIdAsArray });
                this.confirmDialogRef = null;
            }
        });
    }

    moveExperienceUser(experienceUserId: string): void {
        this.engageMoveUsersDialogRef = this._matDialog.open(EngageMoveExperienceMembersDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        const experiences = this._experienceService.getExperiences();
        this.engageMoveUsersDialogRef.componentInstance.title = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.SINGULAR';
        this.engageMoveUsersDialogRef.componentInstance.label = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.LABEL';
        this.engageMoveUsersDialogRef.componentInstance.entityId = this.experienceId;
        this.engageMoveUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserId$;
        this.engageMoveUsersDialogRef.componentInstance.experiences = experiences;

        this.engageMoveUsersDialogRef.afterClosed().subscribe((result: IManageAudienceUsersActionPayload) => {
            if (result) {
                const experienceUserIdAsArray = [{ id: experienceUserId }] as [{ id: string }];
                this._experienceUsersService.moveExperienceUsers(result.target, experienceUserIdAsArray);
                this.engageMoveUsersDialogRef = null;
            }
        });
    }

    /** Whether the number of selected elements matches the total number of rows. */
    get isAllSelected(): boolean {
        if (this.paginator) {
            return this.paginator.pageIndex < this.paginator.getNumberOfPages() - 1
                ? this.selectedUserIds.length === this.paginator.pageSize
                : this.selectedUserIds.length === this.paginator.length - this.paginator.pageIndex * this.paginator.pageSize;
        }
    }

    loadUsersPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadUsers(this.experienceId, this.searchTerm, this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex + 1);
    }
}
